import { ArrowLeft } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { Link, useBlocker } from 'react-router';

import type { Pestanas } from '@/hooks/usePestana';

import { cn } from '@/lib/utils';

import { ConfirmarAccion } from './ConfirmarAccion';
import { Encabezado } from './Estados';

/**
 * Pide confirmar antes de salir si hay cambios sin guardar (navegación interna y cierre de la
 * pestaña del navegador).
 */
export function AvisoSinGuardar({ sucio }: { sucio: boolean }) {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      sucio && currentLocation.pathname !== nextLocation.pathname,
  );
  useEffect(() => {
    if (!sucio) return;
    const fn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', fn);
    return () => window.removeEventListener('beforeunload', fn);
  }, [sucio]);
  return (
    <ConfirmarAccion
      abierto={blocker.state === 'blocked'}
      onCerrar={() => blocker.reset?.()}
      titulo="Tienes cambios sin guardar"
      descripcion="Si sales ahora, se pierden. ¿Salir de todos modos?"
      textoBoton="Salir sin guardar"
      peligro
      onConfirmar={() => blocker.proceed?.()}
    />
  );
}

/**
 * Pantalla de un contenido compuesto (presentación, manual, resumen, clase…): migas, título con
 * su código, acciones y pestañas en `?tab=`.
 */
export function PaginaEditor<P extends Pestanas>({
  volver,
  titulo,
  descripcion,
  acciones,
  pestanas,
  pestana,
  onPestana,
  children,
}: {
  volver: { a: string; texto: string };
  titulo: string;
  descripcion?: string;
  acciones?: ReactNode;
  pestanas?: P;
  pestana?: P[number][0];
  onPestana?: (k: P[number][0]) => void;
  children: ReactNode;
}) {
  return (
    <>
      <Link to={volver.a} className="mb-3 inline-flex items-center gap-1 text-sm font-semibold">
        <ArrowLeft size={16} aria-hidden /> {volver.texto}
      </Link>
      <Encabezado titulo={titulo} descripcion={descripcion} acciones={acciones} />
      {pestanas && pestanas.length > 1 && (
        <div role="tablist" aria-label="Secciones" className="mb-5 flex flex-wrap gap-2">
          {pestanas.map(([k, texto]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={pestana === k}
              className={cn('btn-cs', pestana === k ? 'btn-primario' : 'btn-vidrio')}
              onClick={() => onPestana?.(k)}
            >
              {texto}
            </button>
          ))}
        </div>
      )}
      {children}
    </>
  );
}
